import { useRef } from 'react';
import useInternalLinks from '@/hooks/useInternalLinks';
import errorLogger from '@/utils/errorLogger';
import { zodResolver } from '@hookform/resolvers/zod';
import { graphql, navigate } from 'gatsby';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import requestTypeList from '../constants/requestTypeList';
import Button from '../components/FormButton/FormButton';
import FormError from '../components/FormError/FormError';
import FormRow from '../components/FormRow/FormRow';
import Input from '../components/Input/Input';
import { FormFooter } from '../components/FormLayout/FormLayout';
import { StyledForm } from './LoginSectionForm.styles';
import Select from '../components/Select/Select';
import countryList from '../constants/countryList';
import { loginContactFormSchema } from './validation';
import pushInDataLayer from '@/utils/pushInDataLayer';
import GoogleRecaptcha from '../components/GoogleRecaptcha/GoogleRecaptcha';
import { type ReCAPTCHA } from 'react-google-recaptcha';
import { isTokenRecaptchaValid } from '@/utils/validateGoogleRecaptchaToken';
import getCheqHiddenFieldValue from '../components/CheqHiddenField/getCheqHiddenFieldValue';
import CheqHiddenField from '../components/CheqHiddenField/CheqHiddenField';
import LegalText, { type LegalTextProps } from '../components/LegalText/LegalText';
import useSetGeolocatedCountry from '../hooks/useSetGeolocatedCountry';
import MarketingOptOut from '../components/MarketingOptOut/MarketingOptOut';
import { useLocation } from '@reach/router';

const LoginSectionForm = ({
  loginSectionFormElements: elements,
}: Queries.LOGIN_SECTION_FORMFragment) => {
  const { form_submit_title, success_redirect_page, options, legal_text } = elements;

  const { t } = useTranslation();
  const getInternalLinkURLByCodename = useInternalLinks();
  const {
    register,
    control,
    formState: { errors, isSubmitting },
    setError,
    handleSubmit,
    setValue,
  } = useForm({
    resolver: zodResolver(loginContactFormSchema),
    defaultValues: {
      company: '',
      country: null,
      first_name: '',
      last_name: '',
      email: '',
      form_type: 'contact',
      scope: 'login_sqcloud_enterprise',
      request_type: requestTypeList(t).find((requestType) => requestType.value === 'sales') ?? null,
    },
  });

  useSetGeolocatedCountry({ setValue, field: 'country', countryList });

  const hideLabel = options?.value?.includes('hide_labels') ?? false;
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const redirectURL =
    getInternalLinkURLByCodename(success_redirect_page.value?.system?.codename) ?? '/';
  const { pathname } = useLocation();

  const onSubmit = handleSubmit(async (formValues) => {
    const token = await recaptchaRef.current?.executeAsync();
    await isTokenRecaptchaValid(token ?? null, 'information-form');

    const cq_req_id = getCheqHiddenFieldValue();

    try {
      const requestBody = {
        ...formValues,
        cq_req_id,
      };

      const response = await fetch('/api/submit-form', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'content-type': `application/json`,
        },
      });

      const responseBody = await response.json();

      if (!responseBody.success) {
        throw new Error(responseBody.message);
      }

      pushInDataLayer({
        event: 'submittedForm',
        submittedFormData: {
          type: 'information',
          email: formValues.email,
          country: formValues.country,
          fromPage: pathname,
        },
      });

      navigate(redirectURL);
    } catch (error) {
      errorLogger(error);
      setError('root', { type: 'custom', message: t('form.Could not submit form') });
    }
  });

  return (
    <StyledForm onSubmit={onSubmit} noValidate>
      <FormRow>
        <Input
          type="text"
          id="first_name"
          placeholder={t('form.First Name')}
          formGroupProps={{ label: 'First Name', hideLabel }}
          required
          {...register('first_name')}
          errors={errors}
          disabled={isSubmitting}
        />
        <Input
          type="text"
          id="last_name"
          placeholder={t('form.Last Name')}
          formGroupProps={{ label: 'Last Name', hideLabel }}
          required
          {...register('last_name')}
          errors={errors}
          disabled={isSubmitting}
        />
      </FormRow>
      <Input
        type="email"
        id="email"
        placeholder={t('form.Email')}
        formGroupProps={{ label: 'Email', hideLabel }}
        required
        {...register('email')}
        errors={errors}
        disabled={isSubmitting}
      />
      <Input
        type="text"
        id="company"
        placeholder={t('form.Company Name')}
        formGroupProps={{ label: 'Company Name', hideLabel }}
        required
        {...register('company')}
        errors={errors}
        disabled={isSubmitting}
      />
      <input type="hidden" {...register('form_type')} />
      <input type="hidden" {...register('scope')} />
      <CheqHiddenField />
      <Controller
        name="country"
        control={control}
        render={({ field }) => (
          <Select
            errors={errors}
            required
            id="country"
            placeholder={t('form.Select a country')}
            options={countryList}
            formGroupProps={{ label: 'Country', hideLabel }}
            isDisabled={isSubmitting}
            {...field}
          />
        )}
      />
      <MarketingOptOut
        label={t('form.Marketing Emails Opt Out')}
        errors={errors}
        register={register}
        disabled={isSubmitting}
      />
      <FormFooter isCondensed>
        <Button className="btn" type="submit" variant="primary" isLoading={isSubmitting}>
          {form_submit_title.value}
        </Button>
        <LegalText richText={legal_text as LegalTextProps['richText']} />
        {errors?.root?.message && <FormError>{errors.root.message}</FormError>}
      </FormFooter>
      <GoogleRecaptcha ref={recaptchaRef} />
    </StyledForm>
  );
};

export default LoginSectionForm;

export const fragment = graphql`
  fragment LOGIN_SECTION_FORM on kontent_item_login_section_form {
    __typename
    system {
      codename
      type
      id
    }
    loginSectionFormElements: elements {
      legal_text {
        value
        links {
          link_id
          codename
        }
      }
      form_submit_title {
        value
      }
      success_redirect_page {
        value {
          system {
            codename
            id
          }
          id
          elements {
            url_slug {
              value
            }
          }
        }
      }
      options {
        value
      }
    }
  }
`;
